import { useEffect, useRef, useState } from "react";
import { useBrand } from "../../state/BrandContext.tsx";

/** A person sees only verified memberships; a brand link shows its one brand. */
export function BrandSwitcher() {
  const { brand, memberships, switchBrand, createBrand } = useBrand();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const choices = memberships.filter(b => b.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()));

  useEffect(() => {
    if (!open) return;
    searchInput.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!brand) return null;

  async function submitNewBrand(): Promise<void> {
    if (busy || newName.trim().length < 2) return;
    setBusy(true);
    const created = await createBrand(newName, website);
    setBusy(false);
    if (created) { setCreating(false); setOpen(false); setNewName(""); setWebsite(""); setSearch(""); }
  }

  return (
    <div className="brand-switcher">
      <button ref={trigger} type="button" className="brand-switcher-trigger" aria-expanded={open} aria-controls="brand-switcher-list" onClick={() => setOpen(v => !v)}>
        <span className="brand-switcher-avatar" aria-hidden="true">{brand.name.charAt(0).toLocaleUpperCase()}</span>
        <span className="brand-switcher-label"><strong>{brand.name}</strong><small>{brand.role ?? "Owner"}</small></span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true"><path d="m4 6 4 4 4-4"/></svg>
      </button>
      {open && <div id="brand-switcher-list" className="brand-switcher-panel">
        <label className="sr-only" htmlFor="brand-switcher-search">Search brands</label>
        <input ref={searchInput} id="brand-switcher-search" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search brands" />
        <div className="brand-switcher-choices">
          {choices.map(choice => <button key={choice.id} type="button" className="brand-switcher-choice" aria-current={choice.id === brand.id ? "true" : undefined}
            onClick={() => { setOpen(false); setSearch(""); void switchBrand(choice.id); }}>
            <span className="brand-switcher-avatar" aria-hidden="true">{choice.name.charAt(0).toLocaleUpperCase()}</span>
            <span className="brand-switcher-label"><strong>{choice.name}</strong><small>{choice.role}</small></span>
            {choice.id === brand.id && <span className="brand-switcher-current">Current</span>}
          </button>)}
          {choices.length === 0 && <p className="brand-switcher-empty">No matching brands</p>}
        </div>
        {brand.personal && brand.billingOwner && <div className="brand-switcher-create">
          {!creating ? <button className="brand-switcher-add" type="button" onClick={() => setCreating(true)}>+ Add a brand</button>
            : <form onSubmit={event => { event.preventDefault(); void submitNewBrand(); }}>
              <label htmlFor="brand-switcher-new-name">New brand name</label>
              <input id="brand-switcher-new-name" value={newName} onChange={event => setNewName(event.target.value)} maxLength={100} autoFocus required />
              <label htmlFor="brand-switcher-website">Website <span className="brand-switcher-optional">optional</span></label>
              <input id="brand-switcher-website" type="url" value={website} onChange={event => setWebsite(event.target.value)} placeholder="https://example.com" />
              <p>This account's card and wallet cover this brand. Its plan is separate.</p>
              <div className="brand-switcher-create-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)} disabled={busy}>Cancel</button>
                <button type="submit" className="btn btn-brand" disabled={busy || newName.trim().length < 2}>{busy ? "Creating…" : "Create brand"}</button>
              </div>
            </form>}
        </div>}
      </div>}
    </div>
  );
}
